import React, { useEffect, useMemo, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Loader2, CheckCircle2, QrCode } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { goToLogin } from '../utils/authRedirect'

const DEFAULT_SDK_SCRIPT_URL = '/api/payment/360/sdk'
const LEGACY_SDK_SCRIPT_URL = '/api/payment/360/sdk.js'

function getAuthToken() {
  return localStorage.getItem('token') || ''
}

function loadScriptByUrl(scriptUrl) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = scriptUrl
    script.async = true
    script.dataset.sdk360 = 'true'
    script.dataset.sdk360Src = scriptUrl
    script.onload = () => {
      if (window.SDK360) {
        resolve(window.SDK360)
      } else {
        reject(new Error('360 SDK 已加载但未找到全局对象'))
      }
    }
    script.onerror = () => {
      script.remove()
      reject(new Error(`360 SDK 加载失败: ${scriptUrl}`))
    }
    document.body.appendChild(script)
  })
}

function load360Sdk(scriptUrl = DEFAULT_SDK_SCRIPT_URL) {
  if (window.SDK360) {
    return Promise.resolve(window.SDK360)
  }

  const existingScript = document.querySelector(`script[data-sdk360="true"]`)
  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(window.SDK360))
      existingScript.addEventListener('error', () => reject(new Error('360 SDK 加载失败')))
    })
  }

  return loadScriptByUrl(scriptUrl).catch((error) => {
    if (scriptUrl === LEGACY_SDK_SCRIPT_URL) {
      throw error
    }
    return loadScriptByUrl(LEGACY_SDK_SCRIPT_URL)
  })
}

function init360Sdk(config) {
  if (!window.SDK360) {
    throw new Error('360 SDK 尚未就绪')
  }
  if (!config?.appid || !config?.qid) {
    throw new Error('360 支付配置不完整')
  }
  window.SDK360.init({
    appid: String(config.appid),
    qid: Number(config.qid)
  })
}

function call360CreateOrder(params) {
  return new Promise((resolve, reject) => {
    let finished = false
    const timeoutId = window.setTimeout(() => {
      if (finished) return
      finished = true
      reject(new Error('360 创建订单超时'))
    }, 15000)

    try {
      window.SDK360.createOrder(
        params,
        (res) => {
          if (finished) return
          finished = true
          window.clearTimeout(timeoutId)
          if (Number(res?.errno) === 0 && res?.data?.qr_code) {
            resolve(res)
          } else {
            reject(new Error(res?.errmsg || res?.message || '360 创建订单失败'))
          }
        },
        () => {}
      )
    } catch (error) {
      if (finished) return
      finished = true
      window.clearTimeout(timeoutId)
      reject(error)
    }
  })
}

function call360GetOrderStatus(params) {
  return new Promise((resolve, reject) => {
    let finished = false
    const timeoutId = window.setTimeout(() => {
      if (finished) return
      finished = true
      reject(new Error('360 查单超时'))
    }, 10000)

    try {
      window.SDK360.getOrderStatus(params, (res) => {
        if (finished) return
        finished = true
        window.clearTimeout(timeoutId)
        resolve(res)
      })
    } catch (error) {
      if (finished) return
      finished = true
      window.clearTimeout(timeoutId)
      reject(error)
    }
  })
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, options)
  const payload = await res.json()
  const ok = res.ok && Number(payload?.status) === 1
  return { ok, payload }
}

export default function Test360Pay() {
  const { user, loading: authLoading } = useAuth()
  const [amount, setAmount] = useState('0.01')
  const [sdkReady, setSdkReady] = useState(false)
  const [sdkConfig, setSdkConfig] = useState(null)
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState('idle')
  const [orderNo, setOrderNo] = useState('')
  const [qrCodeUrl, setQrCodeUrl] = useState('')
  const [error, setError] = useState('')
  const [sdkStatusText, setSdkStatusText] = useState('')
  const [sdkScriptUrl, setSdkScriptUrl] = useState(DEFAULT_SDK_SCRIPT_URL)

  const sdkEnabled = useMemo(() => Boolean(sdkConfig?.enabled && sdkReady), [sdkConfig, sdkReady])

  useEffect(() => {
    if (!authLoading && !user) {
      goToLogin('/test-360-pay')
    }
  }, [user, authLoading])

  useEffect(() => {
    let cancelled = false

    async function bootstrap() {
      try {
        const { ok, payload } = await fetchJson('/api/payment/360/config')

        if (!ok) {
          throw new Error(payload?.message || '获取 360 配置失败')
        }

        const config = payload?.data || null
        const resolvedSdkUrl = config?.sdkUrl || DEFAULT_SDK_SCRIPT_URL
        await load360Sdk(resolvedSdkUrl)

        if (cancelled) return
        setSdkReady(true)
        setSdkConfig(config)
        setSdkScriptUrl(resolvedSdkUrl)
      } catch (err) {
        if (cancelled) return
        setError(err.message || '360 支付初始化失败')
      }
    }

    bootstrap()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let timer

    async function checkLocalStatus() {
      const token = getAuthToken()
      const { ok, payload } = await fetchJson(`/api/payment/status/${orderNo}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      if (!ok) return
      if (Number(payload?.data?.status) === 1 || payload?.data?.isPaid) {
        setStatus('success')
      }
    }

    if (status === 'pending' && orderNo) {
      timer = window.setInterval(() => {
        checkLocalStatus().catch((err) => {
          console.error('360 本地订单状态查询失败:', err)
        })
      }, 3000)
    }

    return () => {
      if (timer) window.clearInterval(timer)
    }
  }, [status, orderNo])

  const handleGenerate = async () => {
    if (!amount || Number(amount) <= 0) {
      setError('请输入有效金额')
      return
    }

    setLoading(true)
    setError('')
    setQrCodeUrl('')
    setOrderNo('')
    setSdkStatusText('')
    setStatus('initializing')

    try {
      const token = getAuthToken()
      const { ok, payload } = await fetchJson('/api/payment/test-360-create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: Number(amount),
          subject: 'DClaw 测试360支付'
        })
      })

      if (!ok) {
        throw new Error(payload?.message || '本地下单失败')
      }

      const localOrder = payload?.data || {}
      const currentConfig = localOrder.sdkConfig || sdkConfig
      init360Sdk(currentConfig)

      const sdkOrderParams = {
        order_id: String(localOrder.orderNo),
        order_amount: Number(localOrder.orderAmountFen || Math.round(Number(localOrder.amount || amount) * 100)),
        create_time: String(Math.floor(Date.now() / 1000)),
        product_id: String(localOrder.planId || 'custom_recharge'),
        product_name: localOrder.subject || 'DClaw 充值订单',
        user_id: String(localOrder.userId || user?.id || '')
      }

      const sdkRes = await call360CreateOrder(sdkOrderParams)
      setOrderNo(String(localOrder.orderNo || ''))
      setQrCodeUrl(String(sdkRes?.data?.qr_code || ''))
      setSdkStatusText(JSON.stringify(sdkRes?.data || {}, null, 2))
      setStatus('pending')
    } catch (err) {
      setStatus('idle')
      setError(err.message || '360 支付发起失败')
    } finally {
      setLoading(false)
    }
  }

  const handleCheck360Status = async () => {
    if (!orderNo) return
    setError('')
    try {
      init360Sdk(sdkConfig)
      const res = await call360GetOrderStatus({
        order_id: String(orderNo),
        user_id: String(user?.id || '')
      })
      setSdkStatusText(JSON.stringify(res || {}, null, 2))
    } catch (err) {
      setError(err.message || '360 查单失败')
    }
  }

  if (authLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (!user) return null

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-4 sm:p-8 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-cyan-50/50 dark:bg-cyan-900/20">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <QrCode className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
            360 支付联调测试
          </h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            先本地下单，再由前端 SDK 向 360 发起支付。
          </p>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
              <div className="text-gray-500 dark:text-gray-400">SDK 状态</div>
              <div className="mt-1 font-medium text-gray-900 dark:text-white">{sdkReady ? '已加载' : '加载中'}</div>
            </div>
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
              <div className="text-gray-500 dark:text-gray-400">AppID / QID</div>
              <div className="mt-1 font-medium text-gray-900 dark:text-white">
                {sdkConfig?.appid || '-'} / {sdkConfig?.qid || '-'}
              </div>
            </div>
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
              <div className="text-gray-500 dark:text-gray-400">回调地址</div>
              <div className="mt-1 font-medium text-gray-900 dark:text-white break-all">
                {sdkConfig?.notifyUrl || '-'}
              </div>
            </div>
          </div>

          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 text-sm">
            <div className="text-gray-500 dark:text-gray-400">SDK 地址</div>
            <div className="mt-1 font-medium text-gray-900 dark:text-white break-all">
              {sdkScriptUrl}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              测试支付金额 (元)
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">¥</span>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="0.01"
                step="0.01"
                disabled={loading || status === 'pending'}
                className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-colors disabled:opacity-50"
              />
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              当前测试接口允许小额金额直接创建本地 360 订单。
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-lg border border-red-200 dark:border-red-800">
              {error}
            </div>
          )}

          {status !== 'success' && (
            <button
              onClick={handleGenerate}
              disabled={loading || !sdkEnabled}
              className="w-full py-3 bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-400 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  正在创建 360 订单...
                </>
              ) : (
                '创建 360 支付订单'
              )}
            </button>
          )}

          {orderNo && (
            <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-500 dark:text-gray-400">本地订单号</div>
              <div className="mt-1 font-medium text-gray-900 dark:text-white break-all">{orderNo}</div>
            </div>
          )}

          {status === 'pending' && qrCodeUrl && (
            <div className="flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="bg-white p-3 rounded-xl shadow-sm mb-4">
                <QRCodeSVG value={qrCodeUrl} size={220} />
              </div>
              <p className="text-gray-900 dark:text-white font-medium mb-1">请使用 360 支付扫码</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                正在等待 360 回调更新本地订单...
              </p>
              <div className="mt-4 flex gap-3">
                <button
                  onClick={handleCheck360Status}
                  className="px-4 py-2 text-sm rounded-lg border border-cyan-200 text-cyan-600 hover:bg-cyan-50 dark:border-cyan-800 dark:text-cyan-400 dark:hover:bg-cyan-900/20"
                >
                  查询 360 订单状态
                </button>
                <button
                  onClick={() => {
                    setStatus('idle')
                    setQrCodeUrl('')
                    setOrderNo('')
                    setSdkStatusText('')
                  }}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-900/50"
                >
                  重新创建
                </button>
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center justify-center p-8 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">360 支付成功</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center">
                本地订单已被 360 回调更新为成功，订单号 {orderNo}。
              </p>
              <button
                onClick={() => {
                  setStatus('idle')
                  setQrCodeUrl('')
                  setOrderNo('')
                  setSdkStatusText('')
                }}
                className="px-6 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl font-medium transition-colors"
              >
                继续测试
              </button>
            </div>
          )}

          {sdkStatusText && (
            <div className="p-4 rounded-xl bg-gray-950 text-gray-100 overflow-auto">
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">360 SDK 返回</div>
              <pre className="text-xs whitespace-pre-wrap break-all">{sdkStatusText}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
